import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchTaskById } from '@/store/tasks/tasks.api';
import type { ChecklistItem } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';
import { toast } from '@/utils/toast';

import { toDrafts, type ChecklistDraft } from './checklistDrafts';

/**
 * A task's checklist that saves itself on every change, for `TaskChecklist` on an open task
 * (the task sheet saves its own with the rest of the form). The list shows a change at once
 * and the save follows behind it.
 *
 * Saves run one at a time, each from what the service holds to the list as it is when the
 * save starts, not when it was asked for. Changes made while one runs go with the next, so a
 * step added and ticked at once is created once. A step added here has no id until its save
 * answers; its `draftId` is how the id finds it.
 */
export const useChecklistAutosave = (
  taskId: string,
  incoming: ChecklistItem[] | null | undefined,
) => {
  const saveChecklist = useTasks((state) => state.saveChecklist);
  const [checklist, setChecklist] = useState(() => toDrafts(incoming));
  const syncedJson = useRef(JSON.stringify(incoming ?? []));
  // What the service holds as far as this list knows; every save starts from it.
  const saved = useRef<ChecklistItem[]>(incoming ?? []);
  // The list as last changed here, which the next save brings the service to.
  const latest = useRef(checklist);
  const pending = useRef(0);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const show = useCallback((drafts: ChecklistDraft[]) => {
    latest.current = drafts;
    setChecklist(drafts);
  }, []);

  useEffect(() => {
    const json = JSON.stringify(incoming ?? []);
    if (json === syncedJson.current) return;
    syncedJson.current = json;
    // Mid-save, the copy handed in is the echo of an earlier save and the list here is
    // already ahead of it.
    if (pending.current > 0) return;
    saved.current = incoming ?? [];
    show(toDrafts(incoming, latest.current));
  }, [incoming, show]);

  const onChange = (drafts: ChecklistDraft[]) => {
    show(drafts);
    pending.current += 1;
    queue.current = queue.current
      .then(async () => {
        const target = latest.current;
        if (target === saved.current) return;
        const result = await saveChecklist(taskId, saved.current, target);
        saved.current = result;
        const ids = new Map(result.map((step) => [step.draftId, step.id]));
        show(
          latest.current === target
            ? result
            : latest.current.map((step) => {
                const id = ids.get(step.draftId);
                return id && id !== step.id ? { ...step, id } : step;
              }),
        );
      })
      .catch(async (err: unknown) => {
        console.error(err);
        toast.error('Couldn’t save the checklist. Please try again.', { id: 'checklist-save' });
        // Back to the service's list: a step someone deleted, or a list that changed under a
        // reorder, would only fail again.
        try {
          saved.current = (await fetchTaskById(taskId, { fresh: true })).checklist ?? [];
        } catch (reloadErr: unknown) {
          console.error(reloadErr);
        }
        show(toDrafts(saved.current, latest.current));
      })
      .finally(() => {
        pending.current -= 1;
      });
  };

  return [checklist, onChange] as const;
};
